import {
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData,
    type QueryClient
} from '@tanstack/react-query';
import {
    fetchIamCatalog,
    fetchIamUser,
    fetchIamUsers,
    fetchGrantableRoles,
    createGrant,
    revokeGrant,
    type CreateGrantBody,
    type IamUsersListParams
} from '../services/iam';
import {
    IAM_CATALOG_KEY,
    IAM_GRANTABLE_KEY,
    IAM_ME_KEY,
    IAM_USERS_BASE_KEY,
    IAM_USER_KEY
} from './queryKeys';

// The catalog is a snapshot of code (permissions/roles/routes are declared
// in source, not stored) - a long staleTime avoids refetching it on every
// tab focus while still picking up a deploy within a session.
const CATALOG_STALE_TIME = 5 * 60 * 1000;
const USERS_STALE_TIME = 60 * 1000;
// Same rationale as CATALOG_STALE_TIME: GET /iam/grantable is the code-defined role
// catalog, not stored state.
const GRANTABLE_STALE_TIME = 5 * 60 * 1000;

export function useIamCatalog() {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: IAM_CATALOG_KEY,
        queryFn: fetchIamCatalog,
        staleTime: CATALOG_STALE_TIME
    });

    return {
        catalog: data,
        isLoading,
        isError,
        error,
        refresh: refetch
    };
}

// `retry: false` and the graceful degrade (page renders AccessStateNotice on
// isError) are kept even though the endpoint is live now - a real failure
// here shouldn't retry-storm the API, and the notice reads fine either way.
export function useIamUsersList(params: IamUsersListParams, enabled: boolean) {
    const { data, isLoading, isFetching, isError, error } = useQuery({
        queryKey: [
            ...IAM_USERS_BASE_KEY,
            'list',
            params.page,
            params.limit,
            params.q ?? '',
            params.role ?? '',
            params.sort ?? '',
            params.order ?? ''
        ],
        queryFn: () => fetchIamUsers(params),
        enabled,
        staleTime: USERS_STALE_TIME,
        placeholderData: keepPreviousData,
        retry: false
    });

    return {
        users: data?.data ?? [],
        meta: data?.meta ?? { total: 0, page: params.page, limit: params.limit, totalPages: 1 },
        isLoading,
        isFetching,
        isError,
        error
    };
}

export function useIamUser(userId: string | undefined) {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: IAM_USER_KEY(userId ?? ''),
        queryFn: () => fetchIamUser(userId as string),
        enabled: Boolean(userId),
        staleTime: USERS_STALE_TIME,
        retry: false
    });

    return {
        user: data?.user,
        grants: data?.grants ?? [],
        effective: data?.effective ?? [],
        isLoading: isLoading && Boolean(userId),
        isError,
        error,
        refresh: refetch
    };
}

export function useGrantableRoles() {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: IAM_GRANTABLE_KEY,
        queryFn: fetchGrantableRoles,
        staleTime: GRANTABLE_STALE_TIME
    });

    return {
        roles: data ?? [],
        isLoading,
        isError,
        error
    };
}

// Grant mutations invalidate the /iam/me cache; also both the user detail and
// users list caches, so the drawer and table never disagree after a write.
function invalidateGrantCaches(queryClient: QueryClient, userId: string): void {
    void queryClient.invalidateQueries({ queryKey: IAM_USER_KEY(userId) });
    void queryClient.invalidateQueries({ queryKey: IAM_USERS_BASE_KEY });
    // The grant may be the caller's own - refresh the section gates too.
    void queryClient.invalidateQueries({ queryKey: IAM_ME_KEY });
}

export function useCreateGrant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: CreateGrantBody) => createGrant(body),
        onSuccess: (_data, body) => {
            invalidateGrantCaches(queryClient, body.userId);
        }
    });
}

export function useRevokeGrant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ grantId }: { grantId: string; userId: string }) => revokeGrant(grantId),
        onSuccess: (_data, { userId }) => {
            invalidateGrantCaches(queryClient, userId);
        }
    });
}
